import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, Ref } from 'react';
import { useRef } from 'react';
import type { KaraokeProps, KaraokeRef } from './Karaoke.data';
import styles from './Karaoke.module.scss';
import { karaokeLineReveal, opacity, textParallax } from './Karaoke.transitions';
import Text from '../../atoms/text/Text';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../hooks/useSetupTimeline';

export default function Karaoke({
  className,
  blok,
  ref,
}: KaraokeProps & { ref?: Ref<KaraokeRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const textRef = useRef<HTMLElement>(null);
  const kanjiRef = useRef<HTMLParagraphElement>(null);

  useScrollTrigger({
    trigger: elementRef,
    animation: useSetupTimeline(opacity, { elementRef }),
    vars: {
      scrub: true,
      start: 'top bottom',
      end: 'bottom bottom',
    },
  });

  useScrollTrigger({
    trigger: elementRef,
    animation: useSetupTimeline(textParallax, { textRef }),
    vars: {
      scrub: true,
      start: 'top bottom',
      end: '300%',
    },
  });

  useScrollTrigger({
    trigger: elementRef,
    pin: elementRef,
    animation: useSetupTimeline(karaokeLineReveal, { textRef }),
    vars: {
      pinType: 'transform',
      anticipatePin: 1,
      scrub: true,
      start: 'top top',
      end: '200%',
    },
  });

  return (
    <div ref={elementRef} className={classNames(styles.karaoke, className)} {...sbEditable(blok)}>
      <Text
        as="p"
        size="kanji-large"
        transitionInType="none"
        disableTriggerInOnScroll={false}
        className={styles.backgroundKanji}
        ref={kanjiRef}
        lang="ja"
      >
        {blok.background_kanji}
      </Text>

      <Text
        as="p"
        size="body-standard"
        transitionInType="none"
        disableTriggerInOnScroll={false}
        className={styles.text}
        ref={textRef}
      >
        {blok.text}
      </Text>
    </div>
  );
}
